import { fetchRuntimeConfig } from '@accenturecodefoundry/mfe-pot-shared-runtime-config';
import { initBrowserFeatureFlags, KNOWN_TOGGLES } from '@accenturecodefoundry/mfe-pot-shared-feature-flags';
import { initBrowserObservability } from '@accenturecodefoundry/mfe-pot-shared-observability';

/**
 * Replaces environment.ts/environment.prod.ts + fileReplacements -- see
 * CLAUDE.md's Hosting section. Under plain `nx serve` these dev defaults
 * apply directly; in a container, the entrypoint script injects real values
 * via window.__mfePotEnv from the Helm chart's ConfigMap.
 *
 * Fetched from this app's own origin (not read off window.__mfePotEnv)
 * because this app is loaded as a Native Federation remote inside the
 * shell as often as it's loaded standalone -- when federated, this app's
 * own index.html/env.js never loads (the shell's already-running page
 * just imports this app's JS module into the same window), so
 * window.__mfePotEnv only ever carries the shell's own values. Fetching
 * this app's own env.js directly from its own origin works in both cases.
 */
const devDefaults = {
  strapiBaseUrl: 'http://localhost:1337',
  dashboardBffBaseUrl: 'http://localhost:3004',
  // Base URL for epayroll-ube -- passed to EmploymentSummaryProvider in
  // App.tsx (spec 017-dashboard-new-user-state.md). Dev default is the
  // port `nx serve epayroll-ube` uses (316829_mfe-pot-ePayroll repo).
  // Overridden per-environment via the Helm chart's
  // frontend.runtimeConfig.epayrollUbeBaseUrl, same browser-to-server
  // Ingress-host reasoning as mfe-pot-msca-shell's own runtime-config.ts.
  epayrollUbeBaseUrl: 'http://localhost:3008' as string,
  // undefined -- no local collector needed for plain `nx serve`, see
  // shared-observability's initBrowserObservability.
  otelExporterOtlpEndpoint: undefined as string | undefined,
  // Points at mock-idp's built-in Unleash Frontend API stub so flags resolve
  // to their dev values without a live Unleash server (see mock-idp's
  // GET /api/frontend route). In a container the Helm chart injects the real
  // Unleash URL via env.js, overriding this default. If mock-idp isn't
  // running, unleash-proxy-client catches the error silently and
  // useFeatureFlag falls back to each flag's own `fallback` argument.
  unleashFrontendApiUrl: 'http://localhost:3005/api/frontend' as string | undefined,
  unleashFrontendApiToken: 'insecure-dev-token',
};

export async function loadRuntimeConfig(ownOriginUrl: string) {
  const config = await fetchRuntimeConfig(ownOriginUrl, devDefaults);
  // Single wiring point for every exposed entry point in this app (App.tsx,
  // PaymentHistory.tsx as a standalone widget, ...) -- all funnel through
  // this same function. Idempotent, so calling it more than once per
  // session is safe -- this app exposes both ./Component and
  // ./PaymentHistoryWidget, so it genuinely can be called twice.
  // propagateTraceHeaderCorsUrls matches this app's own BFF Ingress origin
  // on any environment -- see mfe-pot-platform/CLAUDE.md's observability
  // section for why this is required, not optional, for a federated
  // remote's BFF calls.
  initBrowserObservability({
    serviceName: 'dashboard-mfe',
    otlpEndpoint: config.otelExporterOtlpEndpoint,
    propagateTraceHeaderCorsUrls: [/^https?:\/\/dashboard-mfe\./],
  });
  // bootstrap: KNOWN_TOGGLES -- a fresh session with no stored Unleash cache
  // (first-time visitor, or any visitor during an outage) would otherwise
  // see dashboard-overview-cross-domain-widgets' useFeatureFlag fall back to
  // false and lose the cross-domain widget tiles entirely until the first
  // real poll resolves. See shared-feature-flags' known-flags.ts.
  initBrowserFeatureFlags({
    appName: 'dashboard-mfe',
    frontendApiUrl: config.unleashFrontendApiUrl,
    frontendApiToken: config.unleashFrontendApiToken,
    bootstrap: KNOWN_TOGGLES,
  });
  return config;
}
